import React from 'react';
import { wordTools } from './tools';
import type { ToolArgs, WordTool } from './tools';

/**
 * What the packaged chat (`@talk2view/sdk/chat`) asks the host about the host's
 * own tools: what is this call doing, would it change the user's document, and
 * what should the warning say when it would.
 *
 * Nothing in the SDK checks the destructive claim — the chat shows a warning
 * when we say so and shows none when we do not — so this file is the only thing
 * between the agent and an unannounced edit to somebody's document.
 */

const toolByName = new Map<string, WordTool>(wordTools.map((t) => [t.name, t]));

/**
 * Word-aware status text for a tool call, e.g. "Inserting 240 chars at end".
 *
 * Read out of the tool registry rather than a second list kept here, so a tool
 * added to `src/taskpane/tools/` next month is described without anyone
 * remembering this file. All twenty tools declare `getActivityDescription`;
 * one that does not simply falls back to the chat's own rendering.
 */
export function describeToolActivity(name: string, args?: ToolArgs): string | null {
  const describe = toolByName.get(name)?.meta?.getActivityDescription;
  if (!describe) return null;
  try {
    return describe(args ?? {});
  } catch {
    // A broken description must never cost the user their approval card.
    return null;
  }
}

/** `true` → destructive whatever the arguments; a predicate → destructive only for some. */
type DestructiveRule = true | ((args: ToolArgs) => boolean);

/**
 * The seven tools that change what is already in the document, and why.
 *
 * Hand-written, unlike the descriptions above, because "would a person mind if
 * this happened without warning" is a judgement about their document and not a
 * fact the tool can state about itself. `tests/chatConfig.test.ts` cross-checks
 * this set against the registry's own `meta.isDestructive` declarations, so a
 * destructive tool added later cannot be silently left out of it.
 *
 * The other thirteen are either read-only (`get_document`, `get_selection`,
 * `select_text`, `get_comments`) or purely additive — they still ask for
 * approval via `permission: true`, they are just not warned about.
 */
export const DESTRUCTIVE_TOOLS: Record<string, DestructiveRule> = {
  /** Throws away the last change, or reinstates one the user undid on purpose. */
  undo_redo: true,

  /** Its whole job is to remove paragraphs, a range, or every match of a query. */
  delete_content: true,

  /** Replaces the header or footer text of the section outright; there is no append mode. */
  set_header_footer: true,

  /** Writes a page-number field into the header or footer, clearing what was there. */
  insert_page_numbers: true,

  /**
   * Conditional in the registry (`delete_rows`, `delete_columns`, `edit_cell`),
   * unconditional here. Two reasons. The approval card lets the user edit the
   * arguments before approving, but the warning is computed once from the
   * original arguments and never recomputed — so an `add_rows` call edited into
   * a `delete_rows` call would carry no warning at all. And `action` is a free
   * string from the model: anything outside the enum would fall through a
   * per-action check to "not destructive" on a tool that only ever rewrites an
   * existing table. Over-warning on `add_rows` costs a line of amber; the
   * opposite costs table rows.
   */
  edit_table: true,

  /**
   * A plain search reads. Passing `replace_with` turns the same call into a
   * find-and-replace across the document — including `''`, which deletes every
   * match. Matches `search.ts`: the test is that the argument is a string, not
   * merely that the key is present.
   */
  search_document: (args) => typeof args.replace_with === 'string',

  /**
   * `delete` removes a comment and `edit` overwrites its text — both destroy
   * something a person wrote. `reply`, `resolve` and `resolve_with_reply` only
   * add to the thread or change its resolved flag, and are recoverable.
   */
  manage_comment: (args) => args.action === 'delete' || args.action === 'edit',
};

/** Whether a pending tool approval would change something already in the document. */
export function isToolDestructive(name: string, args?: ToolArgs): boolean {
  const rule = DESTRUCTIVE_TOOLS[name];
  if (rule === undefined) return false;
  if (rule === true) return true;
  try {
    return rule(args ?? {});
  } catch {
    // An argument shape we did not anticipate is a reason to warn, not to stay
    // quiet: every conditional tool here is destructive under some arguments.
    return true;
  }
}

/**
 * The line shown above the approve/deny buttons for a destructive call. The
 * chat wraps this in its own `role="alert"` amber box, so this is the sentence
 * and nothing else.
 */
export function destructiveWarning(name: string, activity: string | null): React.ReactNode {
  return (
    <span>
      This will change your document: <strong>{activity ?? name}</strong>. Check the arguments
      above before approving — you can edit them if they are wrong.
    </span>
  );
}
